/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Syncs overview", description = "How syncs work, how to manage them after creation, and where to go to create, schedule, monitor, and recover them.", lastUpdatedDate = new Date(1787184000000), keywords = "backfill, throttle, rate limit";
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Syncs overview",
        description: "How syncs work, how to manage them after creation, and where to go to create, schedule, monitor, and recover them.",
        lastUpdatedDate: "Thu Aug 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How a sync works",
          "depth": 0,
          "id": "how-a-sync-works"
        }, {
          "value": "Choose a task",
          "depth": 0,
          "id": "choose-a-task"
        }, {
          "value": "Sync families",
          "depth": 0,
          "id": "sync-families"
        }, {
          "value": "What varies by destination",
          "depth": 0,
          "id": "what-varies-by-destination"
        }, {
          "value": "Syncs overview page",
          "depth": 0,
          "id": "syncs-overview-page"
        }, {
          "value": "Sync status",
          "depth": 1,
          "id": "sync-status"
        }, {
          "value": "Individual sync overview page",
          "depth": 0,
          "id": "individual-sync-overview-page"
        }, {
          "value": "Resync full query",
          "depth": 1,
          "id": "resync-full-query"
        }, {
          "value": "Clear and fill",
          "depth": 1,
          "id": "clear-and-fill"
        }, {
          "value": "Reset CDC",
          "depth": 1,
          "id": "reset-cdc"
        }, {
          "value": "Run details",
          "depth": 1,
          "id": "run-details"
        }, {
          "value": "Configuration",
          "depth": 1,
          "id": "configuration"
        }, {
          "value": "Schedule",
          "depth": 1,
          "id": "schedule"
        }, {
          "value": "Alerts",
          "depth": 1,
          "id": "alerts"
        }, {
          "value": "Sync logs",
          "depth": 1,
          "id": "sync-logs"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      img: "img",
      h2: "h2",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4",
      pre: "pre",
      code: "code",
      span: "span"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "A sync sends the rows a model returns to a destination, and defines how and when that data lands."
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Audience"
            }), _jsx(_components.th, {
              children: "What this article helps you do"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Data teams"
              })
            }), _jsx(_components.td, {
              children: "Understand how a sync works, then find the task page for creating, scheduling, monitoring, or recovering one."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Marketers"
              })
            }), _jsxs(_components.td, {
              children: ["Activate audiences from ", _jsx(_components.a, {
                href: "/customer-studio/overview",
                children: "Customer Studio"
              }), "; the underlying sync is configured and monitored here."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/syncs-diagram-text.png",
          alt: "Sync to Destination"
        })
      }), "\n", _jsx(_components.h2, {
        id: "how-a-sync-works",
        children: "How a sync works"
      }), "\n", _jsx(_components.p, {
        children: "A sync connects one model to one destination object, list, or table, and describes how the model's rows map into that destination. It has four parts you configure once:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Model"
          }), " — the query results to send."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync type and mode"
          }), " — the destination object to write to and whether to insert, update, or upsert records."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Record matching"
          }), " — the field the destination uses to find the right record."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Field mapping"
          }), " — which model columns land in which destination fields."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Between runs, Hightouch uses ", _jsx(_components.a, {
          href: "/syncs/cdc",
          children: "change data capture"
        }), " to compare the current model results against the previous run and send only the rows that were added, changed, or removed. That's why the first run usually adds every row and later runs send far fewer."]
      }), "\n", _jsx(_components.p, {
        children: "A sync runs on the schedule or trigger you set, then reports the outcome of each run so you can confirm it worked."
      }), "\n", _jsx(_components.h2, {
        id: "choose-a-task",
        children: "Choose a task"
      }), "\n", _jsx(_components.p, {
        children: "Most work with a sync starts from one of these goals:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "If you want to..."
            }), _jsx(_components.th, {
              children: "Go to"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Create a sync from scratch"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/create-your-first-sync",
                children: "Create your first sync"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Choose how records are written to the destination"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/types-and-modes",
                children: "Choose a sync type and mode"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Match source rows to the right destination records"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/record-matching",
                children: "Match records to destination records"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Map model columns to destination fields"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/mapping-data",
                children: "Map model columns to destination fields"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Set when the sync runs"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/schedule-sync-ui",
                children: "Schedule a sync"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Check a run or debug rejected rows"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/debugger",
                children: "Debug a sync run"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Understand why rows retry"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/retries",
                children: "Sync retries"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Get notified when a run fails"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/alerting",
                children: "Configure sync alerts"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Analyze run history in your warehouse"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/warehouse-sync-logs",
                children: "Warehouse Sync Logs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Reprocess or reset a sync's state"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/syncs/resync-reset-clear#full-resync",
                children: "Resync full query"
              }), ", ", _jsx(_components.a, {
                href: "/syncs/resync-reset-clear#reset-cdc",
                children: "Reset CDC"
              }), ", or ", _jsx(_components.a, {
                href: "/syncs/resync-reset-clear#clear-and-fill",
                children: "Clear and fill"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "sync-families",
        children: "Sync families"
      }), "\n", _jsx(_components.p, {
        children: "\"Sync\" is an umbrella term. Most syncs are standard batch syncs that evaluate a model on a schedule, but the semantics differ across families, so a behavior that's true for one isn't always true for another:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Batch syncs"
          }), " — evaluate a model on a schedule or trigger. The default, and what the rest of this page describes."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Audience syncs"
          }), " — created from ", _jsx(_components.a, {
            href: "/customer-studio/overview",
            children: "Customer Studio"
          }), " audiences. The audience defines membership; the sync activates it."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Realtime syncs"
          }), " — react to membership or event changes through ", _jsx(_components.a, {
            href: "/real-time/overview",
            children: "Real-Time Personalization"
          }), " on separate infrastructure, not a batch schedule."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Journey-triggered syncs"
          }), " — run when a ", _jsx(_components.a, {
            href: "/customer-studio/journeys",
            children: "journey"
          }), " evaluates a member, not on a fixed cadence."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "what-varies-by-destination",
        children: "What varies by destination"
      }), "\n", _jsxs(_components.p, {
        children: ["The available sync types, modes, matching keys, fields, delete behavior, and limits are defined by each destination. The sections below cover the shared model; for the specifics of any integration, see its ", _jsx(_components.a, {
          href: "/destinations/overview",
          children: "destination documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "syncs-overview-page",
        children: "Syncs overview page"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lists all syncs, both active and inactive, on the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview"]
        }), " page."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/all-syncs-list.png",
          alt: "The all syncs list in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For each sync, the list shows its ", _jsx(_components.a, {
          href: "/syncs/alerting",
          children: _jsx(_components.strong, {
            children: "alert state"
          })
        }), ", its associated ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: _jsx(_components.strong, {
            children: "model"
          })
        }), " and ", _jsx(_components.a, {
          href: "/getting-started/concepts#destinations",
          children: _jsx(_components.strong, {
            children: "destination"
          })
        }), ", and its ", _jsx(_components.strong, {
          children: "last run"
        }), ". Use the folders and filters alongside the list to narrow it by source, destination, label, or ", _jsx(_components.a, {
          href: "#sync-status",
          children: "status"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Click a sync to open its ", _jsx(_components.a, {
          href: "#individual-sync-overview-page",
          children: "individual overview page"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "sync-status",
        children: "Sync status"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch tracks two kinds of status. ", _jsx(_components.strong, {
          children: "Sync health"
        }), " is the sync's overall condition; ", _jsx(_components.strong, {
          children: "run status"
        }), " is the state of an individual run. The syncs list can show either, and in workspaces with ", _jsx(_components.a, {
          href: "/syncs/alerting",
          children: "advanced alerting"
        }), " it shows an ", _jsx(_components.strong, {
          children: "Alerts"
        }), " column in place of last-run status."]
      }), "\n", _jsx(_components.h4, {
        id: "sync-health",
        children: "Sync health"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Healthy"
          }), ": the most recent run completed"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Warning"
          }), ": the most recent run completed with rejected rows"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Pending"
          }), ": the sync hasn't run yet"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Disabled"
          }), ": the sync is turned off"]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "run-status",
        children: "Run status"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Querying"
          }), ": the run is reading its model"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Preparing"
          }), " / ", _jsx(_components.strong, {
            children: "Processing"
          }), ": the run is preparing data for the destination"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Queued"
          }), ": the run is waiting to start"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "In progress"
          }), ": the run is writing to the destination"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Completed"
          }), ": the run finished"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Completed with errors"
          }), ": the run finished, but the destination rejected some rows"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Interrupted"
          }), ": a temporary interruption stopped the run, and it resumes on the next run"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Aborted"
          }), ": the run was stopped before it finished"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Failed"
          }), ": an error stopped the run. Open the run to see why."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "individual-sync-overview-page",
        children: "Individual sync overview page"
      }), "\n", _jsxs(_components.p, {
        children: ["Click a sync on the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview"]
        }), " to open its own page."]
      }), "\n", _jsxs(_components.p, {
        children: ["The page shows the sync's model and destination, whether it's ", _jsx(_components.strong, {
          children: "enabled"
        }), " or ", _jsx(_components.strong, {
          children: "disabled"
        }), ", a ", _jsx(_components.strong, {
          children: "Run sync"
        }), " button to run it manually, and a ", _jsx(_components.strong, {
          children: "..."
        }), " menu to move the sync to a ", _jsx(_components.a, {
          href: "/workspace-management/folders-filters#folders",
          children: "folder"
        }), ", edit its labels, ", _jsx(_components.a, {
          href: "/syncs/resync-reset-clear#full-resync",
          children: "resync or reset"
        }), " it, or delete it. The ", _jsx(_components.strong, {
          children: "Overview"
        }), " tab summarizes the sync's alert state, its ", _jsx(_components.strong, {
          children: "Recent sync runs"
        }), ", and ", _jsx(_components.strong, {
          children: "Sync metrics"
        }), " such as successful operations over time. The panel alongside it holds the sync's schedule, description, labels, and:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync ID"
          }), ": the numerical identifier used when setting up an ", _jsx(_components.a, {
            href: "/extensions/airflow",
            children: "Airflow"
          }), " operator or the ", _jsx(_components.a, {
            href: "https://github.com/hightouchio/dagster-hightouch",
            children: "Dagster"
          }), ", ", _jsx(_components.a, {
            href: "https://github.com/PrefectHQ/prefect-hightouch",
            children: "Prefect"
          }), ", or ", _jsx(_components.a, {
            href: "https://docs.mage.ai/integrations/hightouch",
            children: "Mage"
          }), " extensions"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync slug"
          }), ": a human-readable identifier for the sync"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Created by"
          }), ": who created the sync, and when"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sync-detail-overview.png",
          alt: "An individual sync's overview page"
        })
      }), "\n", _jsx(_components.p, {
        children: "Use the tabs to view and edit each part of the sync:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Configuration"
          }), ": the sync's ", _jsx(_components.a, {
            href: "#configuration",
            children: "configuration"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Schedule"
          }), ": the sync's ", _jsx(_components.a, {
            href: "#schedule",
            children: "schedule"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Runs"
          }), ": the status, time, and ", _jsx(_components.a, {
            href: "#run-details",
            children: "details"
          }), " of recent runs. Select a run to open the ", _jsx(_components.a, {
            href: "/syncs/debugger",
            children: "debugger"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Alerting"
          }), ": the sync's ", _jsx(_components.a, {
            href: "/syncs/alerting",
            children: "alerts"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sync logs"
          }), ": where the sync's ", _jsx(_components.a, {
            href: "/syncs/warehouse-sync-logs",
            children: "historical logs"
          }), " are stored."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Splits"
          }), ": the ", _jsx(_components.a, {
            href: "/customer-studio/experiments",
            children: "experiment"
          }), " groups assigned to the sync. Appears when the model uses splits."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Activity"
          }), ": recent changes to the sync's configuration and settings."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["These three run-level actions all start from the sync's ", _jsx(_components.strong, {
          children: "..."
        }), " menu → ", _jsx(_components.strong, {
          children: "Resync or reset..."
        }), ". For a decision guide, prerequisites, and warnings, see ", _jsx(_components.a, {
          href: "/syncs/resync-reset-clear",
          children: "Resync, reset CDC, or clear and fill"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "resync-full-query",
        children: "Resync full query"
      }), "\n", _jsxs(_components.p, {
        children: ["A full resync reprocesses every current row as if it were newly ", _jsx(_components.strong, {
          children: "Added"
        }), ", resetting the sync's tracking to its initial state. See ", _jsx(_components.a, {
          href: "/syncs/resync-reset-clear#full-resync",
          children: "when it's safe and how to run it"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "full-resync-prerequisites",
        children: "Full resync prerequisites"
      }), "\n", _jsxs(_components.p, {
        children: ["Before you trigger a full resync, check your ", _jsx(_components.a, {
          href: "/syncs/resync-reset-clear#prerequisites",
          children: "sync mode and delete behavior"
        }), " — reprocessing every row can create duplicates on insert-mode, event, and webhook syncs."]
      }), "\n", _jsx(_components.h3, {
        id: "clear-and-fill",
        children: "Clear and fill"
      }), "\n", _jsxs(_components.p, {
        children: ["Where a destination supports it, ", _jsx(_components.a, {
          href: "/syncs/resync-reset-clear#clear-and-fill",
          children: "clear and fill"
        }), " empties a segment or audience and re-fills it from the current model."]
      }), "\n", _jsx(_components.h3, {
        id: "reset-cdc",
        children: "Reset CDC"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/syncs/resync-reset-clear#reset-cdc",
          children: "Reset CDC"
        }), " clears change-data-capture state without sending any rows, so the next run captures a fresh baseline and sends nothing."]
      }), "\n", _jsx(_components.h3, {
        id: "run-details",
        children: "Run details"
      }), "\n", _jsxs(_components.p, {
        children: ["A run is a single invocation of a sync, triggered manually or on a schedule. The ", _jsx(_components.strong, {
          children: "Runs"
        }), " tab lists each run with:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The run's ", _jsx(_components.strong, {
            children: "status"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["The time the run ", _jsx(_components.strong, {
            children: "started"
          }), " and its duration"]
        }), "\n", _jsxs(_components.li, {
          children: ["The number of ", _jsx(_components.strong, {
            children: "rows queried"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["The total ", _jsx(_components.strong, {
            children: "operations"
          }), " — expand it to see rows added, changed, and removed"]
        }), "\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "results"
          }), ": successful operations in green and rejected operations in red"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sync-run-list.png",
          alt: "The runs list for a sync"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Click ", _jsx(_components.strong, {
          children: "View run"
        }), " to open a run. Its rejected rows appear on the run's ", _jsx(_components.strong, {
          children: "Rejected"
        }), " tab, each with the destination's error message. To inspect a row's request and response, use the ", _jsx(_components.a, {
          href: "/syncs/debugger",
          children: "debugger"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/run-rejected-rows.png",
          alt: "A run's rejected rows with error messages"
        })
      }), "\n", _jsx(_components.h3, {
        id: "configuration",
        children: "Configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Configuration"
        }), " tab exposes destination-specific options. These vary by destination but generally include:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Type"
          }), ": the kind of object to sync to, such as objects, events, or subscription lists. See ", _jsx(_components.a, {
            href: "/syncs/types-and-modes",
            children: "types and modes"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Mode"
          }), ": the update mode, such as upsert, insert, or update. See ", _jsx(_components.a, {
            href: "/syncs/types-and-modes",
            children: "types and modes"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Record matching"
          }), ": for update or upsert modes, how source rows match destination records. See ", _jsx(_components.a, {
            href: "/syncs/record-matching",
            children: "record matching"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Field mapping"
          }), ": which source columns sync to which destination fields. See ", _jsx(_components.a, {
            href: "/syncs/mapping-data",
            children: "field mapping"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Batching"
          }), ": how data is batched into API requests."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Delete behavior"
          }), ": what to do when a record leaves your source."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "For some sync types and destinations, you may want to remove records that no longer appear in your model's query results."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, you might sync customer records to a CRM from a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " of all active users. When a user becomes inactive and drops out of the query results, you may want to delete them or clear their fields in the CRM."]
      }), "\n", _jsx(_components.p, {
        children: "Depending on the destination and sync mode, common delete-behavior options are:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the record in the destination. Use this when you want the destination to keep accruing records from your source."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear fields"
              })
            }), _jsx(_components.td, {
              children: "Keep the record but clear the mapped fields. Use this to retain records while emptying the fields your sync maps."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete destination record"
              })
            }), _jsx(_components.td, {
              children: "Delete the record in the destination. Use this when you want the destination to mirror your source by dropping records that leave your model."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch acts on records only as they leave your model's query results, and only ", _jsx(_components.em, {
          children: "after"
        }), " you apply deletion settings. It has no access to historically removed records, so it can't delete or clear records that already left your source before this run. It compares against the immediately previous run only. A ", _jsx(_components.a, {
          href: "/syncs/resync-reset-clear#full-resync",
          children: "full resync"
        }), " doesn't delete or clear historically removed records."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Regardless of the delete-behavior option, a ", _jsx(_components.a, {
            href: "/syncs/debugger#view-run-details",
            children: "run's detail\npage"
          }), " always displays removed rows."]
        })
      }), "\n", _jsx(_components.p, {
        children: "For a destination's delete-behavior details, see its documentation."
      }), "\n", _jsx(_components.h4, {
        id: "configuration-actions",
        children: "Configuration actions"
      }), "\n", _jsxs(_components.p, {
        children: ["From the configuration, you can also ", _jsx(_components.a, {
          href: "#test-a-row",
          children: "test a row"
        }), " or edit the configuration as a ", _jsx(_components.a, {
          href: "#edit-as-json",
          children: "JSON"
        }), " object."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sync-config-actions.png",
          alt: "The Test and Edit as JSON actions on a sync's configuration"
        })
      }), "\n", _jsx(_components.h4, {
        id: "test-a-row",
        children: "Test a row"
      }), "\n", _jsx(_components.p, {
        children: "Testing a row sends a single row from your model to your destination, so you can confirm the sync works before running it in full."
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Test"
        }), " button appears at the top of the configuration when your destination supports row testing for the current setup. If you don't see it, the destination doesn't support testing this configuration — some require specific options first, such as Meta Custom Audiences, which tests only against an existing audience."]
      }), "\n", _jsxs(_components.p, {
        children: ["Click ", _jsx(_components.strong, {
          children: "Test"
        }), " and select a row. Before testing, you can edit a column's data type and value by hovering over it, clicking the pencil icon, making the change, and clicking the check mark. You can't edit the model column used for ", _jsx(_components.a, {
          href: "/syncs/record-matching#choose-the-model-column",
          children: "record matching"
        }), ", because Hightouch ", _jsx(_components.a, {
          href: "/models/data-types-casting",
          children: "intentionally stringifies"
        }), " it for performance."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/test-row-modal.png",
          alt: "The Test a row window with a selected row and its column values"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The row-testing window displays only certain ", _jsx(_components.a, {
            href: "/models/data-types-casting#data-types",
            children: "data\ntypes"
          }), ", such as ", _jsx(_components.strong, {
            children: "string"
          }), " and\n", _jsx(_components.strong, {
            children: "number"
          }), ". A column of another type, such as ", _jsx(_components.strong, {
            children: "date"
          }), ", shows as ", _jsx(_components.strong, {
            children: "string"
          }), ".\nThis affects the UI only — Hightouch doesn't convert the data type when\nsyncing. To confirm a column's type, see the ", _jsx(_components.a, {
            href: "/models/data-types-casting#view-model-data-types",
            children: "model\nconfiguration"
          }), " page."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Click ", _jsx(_components.strong, {
          children: "Sync as added row"
        }), " to send it, or use the ", _jsx(_components.strong, {
          children: "..."
        }), " menu to sync it as a ", _jsx(_components.a, {
          href: "/syncs/debugger#view-run-details",
          children: "changed or removed row"
        }), ". The request and response appear alongside the row so you can see exactly what Hightouch sent and what the destination returned. The UI may take a few seconds to show results."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/test-row-response.png",
          alt: "The Test a row window showing the request and response for a synced row"
        })
      }), "\n", _jsx(_components.h4, {
        id: "edit-as-json",
        children: "Edit as JSON"
      }), "\n", _jsx(_components.p, {
        children: "You can edit a sync's configuration as JSON to make several changes at once or copy one sync's configuration to another. For example:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-JSON",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"mode\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"upsert\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"object\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"fromId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"track_visitor_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"object\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"user\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"mappings\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"to\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"uuid-western\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"from\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"uuid\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"to\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Trialer Status\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"from\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"trialer_status\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"customMappings\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"to\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"unique_notification_status\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n            ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"from\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"notify_status\""
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "schedule",
        children: "Schedule"
      }), "\n", _jsx(_components.p, {
        children: "Every sync has a schedule, which decides how often it runs. Besides manual runs, you can schedule a sync with these options:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Interval"
          }), ": run on a set interval, such as once a day."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom recurrence"
          }), ": run at a specific date and time, such as every Monday at 9 AM."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Cron expression"
          }), ": run on a ", _jsx(_components.a, {
            href: "https://en.wikipedia.org/wiki/Cron",
            children: "cron expression"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "dbt Cloud"
          }), ": trigger runs from ", _jsx(_components.a, {
            href: "/extensions/dbt-cloud",
            children: "dbt Cloud"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Fivetran"
          }), ": trigger runs from ", _jsx(_components.a, {
            href: "/extensions/fivetran",
            children: "Fivetran"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/sync-schedule.png",
          alt: "Sync scheduling in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can also trigger syncs with ", _jsx(_components.a, {
          href: "/extensions/airflow",
          children: "Airflow"
        }), ", ", _jsx(_components.a, {
          href: "https://github.com/hightouchio/dagster-hightouch",
          children: "Dagster"
        }), ", ", _jsx(_components.a, {
          href: "https://docs-v1.prefect.io/api/latest/tasks/hightouch.html",
          children: "Prefect"
        }), ", ", _jsx(_components.a, {
          href: "https://docs.mage.ai/integrations/hightouch",
          children: "Mage"
        }), ", or the ", _jsx(_components.a, {
          href: "/api-reference",
          children: "REST API"
        }), ", even though these aren't listed as schedule types. For all scheduling options, see ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-ui",
          children: "schedule a sync"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To require one sync to finish before another begins, use ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-with-sequences",
          children: "Sequences"
        }), ". If you're planning around Daylight Saving Time, review the ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-ui#daylight-saving-time-dst",
          children: "DST guidance"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "alerts",
        children: "Alerts"
      }), "\n", _jsxs(_components.p, {
        children: ["Configure ", _jsx(_components.a, {
          href: "/syncs/alerting",
          children: "alerting"
        }), " per sync from the ", _jsx(_components.strong, {
          children: "Alerting"
        }), " tab."]
      }), "\n", _jsx(_components.h3, {
        id: "sync-logs",
        children: "Sync logs"
      }), "\n", _jsx(BtOnly, {
        featureName: "Warehouse Sync Logs",
        plural: true
      }), "\n", _jsxs(_components.p, {
        children: ["Configure ", _jsx(_components.a, {
          href: "/syncs/warehouse-sync-logs",
          children: "Warehouse Sync Logs"
        }), " from the ", _jsx(_components.strong, {
          children: "Sync logs"
        }), " tab. This writes the results of your syncs back into your warehouse for more complex analysis."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
